import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Check, Loader2, ShieldCheck, Sparkles } from 'lucide-react';
import { gameshowApi, GameShowApiError, type PublicSponsorship } from '@/services/gameshowApi';

// ── /sponsor/:token ───────────────────────────────────────────────────
// PUBLIC sponsor-facing pay page (no auth). Translates the RIGHT panel of
// Game Show - Sponsorship.dc.html ("What the sponsor sees") into Hey Joe
// tokens: episode art band, show title, the host's free-text "what you get",
// the price, a name + email form, and a gold "Sponsor this show" CTA that
// redirects to Stripe Checkout. Handles ?paid=1 / ?canceled=1 / 404.

const PAGE_BG = 'radial-gradient(120% 70% at 50% -6%, #2a1c3c 0%, #16110f 52%, #0b0908 100%)';
const GOLD = '#ffd600';
const GREEN = '#8cc63f';

const kicker: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  fontWeight: 600,
};

// "$1,000" for whole dollars, "$1,000.50" otherwise.
function formatUsd(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0
    ? `$${dollars.toLocaleString()}`
    : `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// The offer is free-text; split multi-line offers into check-bulleted perks,
// otherwise render the single line as one perk.
function offerLines(text: string): string[] {
  const lines = text
    .split('\n')
    .map((l) => l.replace(/^[-•*]\s*/, '').trim())
    .filter(Boolean);
  return lines.length ? lines : [text.trim()];
}

const SponsorPayPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [params, setParams] = useSearchParams();
  const paid = params.get('paid') === '1';
  const canceled = params.get('canceled') === '1';

  const [offer, setOffer] = useState<PublicSponsorship | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sponsorName, setSponsorName] = useState('');
  const [sponsorEmail, setSponsorEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await gameshowApi.sponsorOffer(token);
      setOffer(data);
      setNotFound(false);
    } catch (e) {
      if (e instanceof GameShowApiError && e.status === 404) setNotFound(true);
      else setError(e instanceof Error ? e.message : 'Could not load this sponsorship.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const clearCanceled = () => {
    const next = new URLSearchParams(params);
    next.delete('canceled');
    setParams(next, { replace: true });
  };

  const submit = async () => {
    if (!token || submitting) return;
    if (!sponsorName.trim() || !sponsorEmail.trim()) {
      setError('Enter your name and email to continue.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { url } = await gameshowApi.sponsorCheckout(token, sponsorName.trim(), sponsorEmail.trim());
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout.');
      setSubmitting(false);
    }
  };

  // ── shells ──
  if (loading) {
    return (
      <Shell>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,.6)', padding: 60 }}>
          <Loader2 size={18} className="animate-spin" /> Loading sponsorship…
        </div>
      </Shell>
    );
  }

  if (notFound || !offer) {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '54px 20px', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
          <div style={{ ...kicker, color: GOLD }}>Link unavailable</div>
          <h1 className="font-hj-display" style={{ fontSize: 30, margin: 0, color: '#fff', lineHeight: 1.1 }}>
            This sponsorship link is no longer available
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,.7)', maxWidth: 340, lineHeight: 1.45, margin: 0 }}>
            The offer may have been withdrawn or already funded. Check with the host for a fresh link.
          </p>
        </div>
      </Shell>
    );
  }

  // Paid: thank-you state (either the sponsorship is already paid, or we're
  // back from a successful checkout redirect).
  if (paid || offer.status === 'paid') {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '54px 22px', display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
          <span
            style={{
              width: 68,
              height: 68,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(140,198,63,.14)',
              border: '1px solid rgba(140,198,63,.5)',
            }}
          >
            <Check size={30} color={GREEN} />
          </span>
          <div style={{ ...kicker, color: GREEN }}>You're sponsoring the show</div>
          <h1 className="font-hj-display" style={{ fontSize: 32, margin: 0, color: GOLD, lineHeight: 1.05 }}>
            {offer.showTitle}
          </h1>
          <p style={{ fontSize: 14.5, color: 'var(--hj-ink)', maxWidth: 360, lineHeight: 1.5, margin: 0 }}>
            Thank you for backing this show. {offer.hostName} will confirm your placement, and your receipt is on its way by email.
          </p>
        </div>
      </Shell>
    );
  }

  const perks = offerLines(offer.offerText);

  return (
    <Shell>
      <div style={{ padding: 0 }}>
        {/* episode art band */}
        <div
          style={{
            position: 'relative',
            aspectRatio: '16 / 9',
            background: 'repeating-linear-gradient(135deg, #241a30 0 12px, #1c1526 12px 24px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--hj-font-mono)',
              fontSize: 10,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,.4)',
            }}
          >
            episode art
          </span>
          <div
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(242,134,201,.9)',
              color: '#fff',
              ...kicker,
              fontSize: 9,
              padding: '5px 9px',
              borderRadius: 7,
            }}
          >
            <Sparkles size={11} /> Sponsorship
          </div>
        </div>

        <div style={{ padding: '22px 22px 26px' }}>
          {canceled && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                fontSize: 12.5,
                color: '#ffd7a1',
                background: 'rgba(255,157,63,.1)',
                border: '1px solid rgba(255,157,63,.35)',
                borderRadius: 11,
                padding: '10px 12px',
                marginBottom: 16,
              }}
            >
              Checkout canceled — you haven't been charged.
              <button
                onClick={clearCanceled}
                style={{ cursor: 'pointer', border: 'none', background: 'transparent', color: '#ffd7a1', fontFamily: 'var(--hj-font-ui)', fontWeight: 700, fontSize: 12 }}
              >
                Dismiss
              </button>
            </div>
          )}

          <div style={{ ...kicker, color: GOLD }}>Sponsor this show</div>
          <h1 className="font-hj-display" style={{ fontSize: 30, margin: '5px 0 0', color: '#fff', lineHeight: 1.05 }}>
            {offer.showTitle}
          </h1>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', marginTop: 6 }}>
            Hosted by <b style={{ color: 'var(--hj-ink)' }}>{offer.hostName}</b>
          </div>

          <div style={{ height: 1, background: 'rgba(255,255,255,.09)', margin: '18px 0' }} />

          {/* what you get */}
          <div style={{ ...kicker, letterSpacing: '0.14em', color: 'rgba(255,255,255,.5)', marginBottom: 11 }}>What you get</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {perks.map((perk, i) => (
              <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                <span
                  style={{
                    flexShrink: 0,
                    width: 19,
                    height: 19,
                    borderRadius: '50%',
                    background: 'rgba(140,198,63,.16)',
                    border: '1px solid rgba(140,198,63,.5)',
                    color: GREEN,
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: 1,
                  }}
                >
                  <Check size={11} />
                </span>
                <span style={{ fontSize: 14.5, lineHeight: 1.4, color: 'var(--hj-ink)' }}>{perk}</span>
              </div>
            ))}
          </div>

          <div style={{ height: 1, background: 'rgba(255,255,255,.09)', margin: '18px 0' }} />

          {/* price */}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
            <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)' }}>Sponsorship</span>
            <span className="font-hj-display tabular" style={{ fontSize: 34, color: '#fff' }}>
              {formatUsd(offer.amountCents)}
            </span>
          </div>

          {/* sponsor form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              value={sponsorName}
              onChange={(e) => setSponsorName(e.target.value)}
              placeholder="Your name or company"
              style={inputStyle}
            />
            <input
              value={sponsorEmail}
              onChange={(e) => setSponsorEmail(e.target.value)}
              type="email"
              placeholder="Email for your receipt"
              style={inputStyle}
            />
          </div>

          {error && (
            <div style={{ fontSize: 12.5, color: '#ffb9c6', marginTop: 10, lineHeight: 1.4 }}>{error}</div>
          )}

          <button
            onClick={submit}
            disabled={submitting}
            style={{
              width: '100%',
              marginTop: 14,
              cursor: submitting ? 'default' : 'pointer',
              border: 'none',
              borderRadius: 14,
              padding: 15,
              fontFamily: 'var(--hj-font-ui)',
              fontWeight: 700,
              fontSize: 15,
              color: '#150e20',
              background: 'linear-gradient(90deg,#ffe566,#ffd600)',
              opacity: submitting ? 0.6 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 9,
            }}
          >
            {submitting ? <Loader2 size={17} className="animate-spin" /> : null}
            {submitting ? 'Starting checkout…' : `Sponsor this show · ${formatUsd(offer.amountCents)}`}
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              fontSize: 11.5,
              color: 'rgba(255,255,255,.45)',
              marginTop: 11,
            }}
          >
            <ShieldCheck size={13} /> Secure checkout · receipt emailed · host confirms your placement
          </div>
        </div>
      </div>
    </Shell>
  );
};

const inputStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  background: 'rgba(0,0,0,.28)',
  border: '1px solid rgba(255,255,255,.14)',
  borderRadius: 11,
  padding: '13px 14px',
  color: 'var(--hj-ink)',
  fontFamily: 'var(--hj-font-ui)',
  fontSize: 14.5,
  outline: 'none',
};

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      minHeight: '100vh',
      background: PAGE_BG,
      color: 'var(--hj-ink)',
      fontFamily: 'var(--hj-font-ui)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      padding: '32px 16px 48px',
    }}
  >
    <div
      style={{
        width: '100%',
        maxWidth: 440,
        borderRadius: 20,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,.1)',
        background: 'rgba(20,16,24,.6)',
        boxShadow: '0 30px 80px -30px rgba(0,0,0,.7)',
      }}
    >
      {children}
    </div>
  </div>
);

export default SponsorPayPage;
